// Companion/blocks/addedBlockScroll.ts
import { useCallback, useEffect, useState } from 'react';
import type { ChatBlock } from '@/slices/chat/chatSlice';

/**
 * Going back to a block a badge already added (ORA-546/547), rather than
 * adding a second one.
 */

/**
 * The question block already serving `variantId` in this thread, or null.
 *
 * Only blocks of messages the conversation is actually showing count: the
 * slice keeps the messages a regenerate superseded in its id map, and a block
 * of one of those is not on screen to scroll to. The badge's own block is
 * skipped as well — when the scheduled variant is the one the student is
 * looking at, the block to take them to is the added one, not this one.
 */
export const findAddedVariantBlockId = (params: {
  blocks: ChatBlock[];
  /** `conversations[id].messageIds` — the thread as it is rendered. */
  visibleMessageIds: readonly string[];
  variantId: string;
  /** The served question block the badge sits on. */
  sourceBlockId: string;
}): string | null => {
  const visible = new Set(params.visibleMessageIds);

  return (
    params.blocks.find(
      (block) =>
        block.id !== params.sourceBlockId &&
        block.kind === 'question' &&
        block.payload.variantId === params.variantId &&
        visible.has(block.messageId)
    )?.id ?? null
  );
};

/** How long to wait for the added block to be committed, in frames. */
const MAX_SCROLL_FRAMES = 30;

/** The element a rendered block draws itself into (`ChatBlocks`). */
const blockElement = (blockId: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[data-block-id="${CSS.escape(blockId)}"]`
  );

/** The nearest ancestor that can actually scroll, if any. */
const scrollParentOf = (element: HTMLElement): HTMLElement | null => {
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const scrolls = overflowY === 'auto' || overflowY === 'scroll';
    if (scrolls && current.scrollHeight > current.clientHeight) return current;
    current = current.parentElement;
  }

  return null;
};

/** The utility class for the ring pulse (`--animate-chat-flash-ring`). */
export const BLOCK_FLASH_CLASS = 'animate-chat-flash-ring';

/**
 * Pulse a ring around the block once, so the student sees which block a chip
 * meant even when it was already in view and nothing scrolled (ORA-628: a
 * "Shown above" tap that visibly did nothing read as broken). Re-adding the
 * class restarts the pulse on a repeat tap.
 */
const flashBlockElement = (element: HTMLElement): void => {
  element.classList.remove(BLOCK_FLASH_CLASS);
  // Force a style flush so removing and re-adding the class restarts it.
  void element.offsetWidth;
  element.classList.add(BLOCK_FLASH_CLASS);

  element.addEventListener(
    'animationend',
    () => element.classList.remove(BLOCK_FLASH_CLASS),
    { once: true }
  );
};

/**
 * Bring the block into view and pulse it, or report that it is not on screen
 * yet.
 *
 * The thread is its own scroll container, so that container is what moves:
 * `scrollIntoView` would scroll whatever ancestor could, including the page
 * behind the drawer (see `ChatThread`).
 *
 * Exported for the answer footer (ORA-601), whose "Shown above" chip takes the
 * student back to a block that is already rendered — one that is in the DOM
 * right now, so it needs the single look rather than the retrying hook below.
 */
export const scrollToBlockElement = (blockId: string): boolean => {
  const element = blockElement(blockId);
  if (!element) return false;

  flashBlockElement(element);

  const container = scrollParentOf(element);
  if (!container) return true;

  const offset =
    element.getBoundingClientRect().top - container.getBoundingClientRect().top;

  container.scrollTo({
    top: container.scrollTop + offset - 16,
    behavior: 'smooth',
  });

  return true;
};

/**
 * Scroll to a block that is not in the DOM yet, retrying for a few frames.
 *
 * The thread has to render first — a message reaching the slice, a re-render,
 * the registry resolving the payload — so looking once and giving up silently
 * does nothing at all. Returns a cancel function for the caller's cleanup.
 *
 * Shared by the hook below and by the surfaces that reopen on a block after a
 * study set (ORA-681), where the thread is still being fetched when the
 * request is made.
 */
export const scrollToBlockWhenRendered = (blockId: string): (() => void) => {
  let frame = 0;
  let attempts = 0;

  const tick = () => {
    attempts += 1;
    if (scrollToBlockElement(blockId)) return;
    if (attempts >= MAX_SCROLL_FRAMES) return;
    frame = window.requestAnimationFrame(tick);
  };

  frame = window.requestAnimationFrame(tick);
  return () => window.cancelAnimationFrame(frame);
};

/** One request to scroll; the count makes a repeat tap on the same block count. */
interface ScrollRequest {
  blockId: string;
  nonce: number;
}

/**
 * Ask for an added block to be scrolled to.
 *
 * The block is added by a network response, so it is not in the DOM when the
 * handler returns and may not be after the next frame either — the message has
 * to reach the slice, the thread has to re-render, and the registry has to
 * resolve the payload. So the request is state, the scroll runs from an effect
 * keyed on it, and it retries for a few frames before giving up rather than
 * looking once and silently doing nothing.
 */
export const useScrollToAddedBlock = (): ((blockId: string) => void) => {
  const [request, setRequest] = useState<ScrollRequest | null>(null);

  useEffect(() => {
    if (!request) return;
    return scrollToBlockWhenRendered(request.blockId);
  }, [request]);

  return useCallback(
    (blockId: string) =>
      setRequest((current) => ({ blockId, nonce: (current?.nonce ?? 0) + 1 })),
    []
  );
};
